import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore'
import { getFirebaseDb, isFirebaseAvailable } from '../lib/firebaseInit'
import {
  CHAT_ERRORS,
  CHAT_FIELDS,
  CHAT_PATHSEGMENTS,
  CHATROOM_PATH,
  USERS_COLLECTION,
} from '@/constants/chat'
import type { ChatContact } from '@/schemas/chat.schema'
import { getUserById } from '@/api/userApi'
import type { User } from '@/schemas/user.schema'

export const initializeChatRoom = async (
  roomId: string,
  participants: string[] = [],
  chatAdminId?: string
) => {
  if (!isFirebaseAvailable()) {
    throw new Error(CHAT_ERRORS.SERVICE_UNAVAILABLE)
  }

  const db = getFirebaseDb()
  if (!db) {
    throw new Error(CHAT_ERRORS.SERVICE_UNAVAILABLE)
  }

  try {
    const roomRef = doc(db, CHATROOM_PATH, roomId)
    const roomDoc = await getDoc(roomRef)

    if (!roomDoc.exists()) {
      const roomData: Record<string, unknown> = {
        id: roomId,
        participants,
        createdAt: new Date().toISOString(),
        lastMessage: '',
        lastMessageTime: null,
        isActive: true,
      }

      if (chatAdminId) {
        roomData.chatAdminId = chatAdminId
      }

      await setDoc(roomRef, roomData)
    }
  } catch (error) {
    console.error('Error initializing chat room:', error)
    throw error
  }
}

export const getUserChatRooms = async (userId: string) => {
  if (!isFirebaseAvailable()) {
    throw new Error(CHAT_ERRORS.SERVICE_UNAVAILABLE)
  }

  const db = getFirebaseDb()
  if (!db) {
    throw new Error(CHAT_ERRORS.SERVICE_UNAVAILABLE)
  }

  try {
    const chatRoomsRef = collection(db, CHATROOM_PATH)
    const firebaseQuery = query(
      chatRoomsRef,
      where(CHAT_FIELDS.PARTICIPANTS, 'array-contains', userId)
    )

    const querySnapshot = await getDocs(firebaseQuery)
    const rooms = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }))

    const roomsByParticipant = new Map<string, Record<string, unknown>>()

    rooms.forEach((room: Record<string, unknown>) => {
      const participants = room.participants as string[] | undefined
      const otherParticipantId = participants?.find((p: string) => p !== userId)

      if (!otherParticipantId) return

      const existingRoom = roomsByParticipant.get(otherParticipantId)

      const roomTime = getRoomTimestamp(room)
      const existingTime = existingRoom ? getRoomTimestamp(existingRoom) : 0

      if (!existingRoom || roomTime > existingTime) {
        roomsByParticipant.set(otherParticipantId, room)
      }
    })

    const uniqueRooms = Array.from(roomsByParticipant.values())
    uniqueRooms.sort((a, b) => {
      const timeA = getRoomTimestamp(a)
      const timeB = getRoomTimestamp(b)
      return timeB - timeA
    })

    return uniqueRooms
  } catch (error) {
    console.error('Error fetching user chat rooms:', error)
    throw error
  }
}

export function getRoomTimestamp(room: Record<string, unknown>): number {
  const lastMessageTime = room.lastMessageTime

  if (!lastMessageTime) {
    if (room.createdAt) {
      return new Date(room.createdAt as string).getTime()
    }
    return 0
  }

  if (
    typeof lastMessageTime === 'object' &&
    lastMessageTime !== null &&
    'toMillis' in lastMessageTime
  ) {
    return (lastMessageTime as { toMillis: () => number }).toMillis()
  }

  if (typeof lastMessageTime === 'number') {
    return lastMessageTime
  }

  if (typeof lastMessageTime === 'string') {
    return new Date(lastMessageTime).getTime()
  }

  return 0
}

export interface ChatUserData {
  id: string
  name: string
  profilePicture: {
    url: string
    key: string
    expiresOn: string
  } | null
  lastFetched: number
}

export const getChatUserData = async (
  userId: string
): Promise<ChatUserData | null> => {
  if (!isFirebaseAvailable()) {
    return fetchUserFromApi(userId)
  }

  const db = getFirebaseDb()
  if (!db) {
    return fetchUserFromApi(userId)
  }

  try {
    const userDocRef = doc(db, USERS_COLLECTION, userId)
    const userDoc = await getDoc(userDocRef)

    if (userDoc.exists()) {
      const cachedData = userDoc.data() as ChatUserData
      return cachedData
    }

    const freshData = await fetchUserFromApi(userId)

    if (freshData) {
      await setDoc(userDocRef, {
        ...freshData,
        updatedAt: serverTimestamp(),
      })
    }

    return freshData
  } catch (error) {
    console.error('Error getting chat user data:', error)
    throw error
  }
}

export const syncChatUserData = async (userId: string): Promise<void> => {
  if (!isFirebaseAvailable()) {
    return
  }
  const db = getFirebaseDb()
  if (!db) {
    return
  }

  try {
    const userDocRef = doc(db, USERS_COLLECTION, userId)
    const userDoc = await getDoc(userDocRef)

    if (userDoc.exists()) {
      const freshData = await fetchUserFromApi(userId)

      if (freshData) {
        await setDoc(userDocRef, {
          ...freshData,
          updatedAt: serverTimestamp(),
        })
      }
    }
  } catch (error) {
    console.error('Error syncing chat user data:', error)
    throw error
  }
}

const fetchUserFromApi = async (
  userId: string
): Promise<ChatUserData | null> => {
  try {
    const response = await getUserById(userId)
    const userData: User = response.data

    return {
      id: userData.id,
      name: `${userData.firstName} ${userData.lastName}`,
      profilePicture: userData.profilePicture?.url
        ? {
            url: userData.profilePicture.url,
            key: userData.profilePicture.key,
            expiresOn: userData.profilePicture.expiresOn,
          }
        : null,
      lastFetched: Date.now(),
    }
  } catch (error) {
    console.error('Error fetching user from API:', error)
    return null
  }
}

export const updateChatUserCache = async (
  userId: string,
  data: Partial<ChatUserData>
): Promise<void> => {
  if (!isFirebaseAvailable()) return

  const db = getFirebaseDb()
  if (!db) return

  try {
    const userDocRef = doc(db, USERS_COLLECTION, userId)
    const currentDoc = await getDoc(userDocRef)

    if (currentDoc.exists()) {
      await setDoc(
        userDocRef,
        {
          ...data,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      )
    }
  } catch (error) {
    console.error('Error updating chat user cache:', error)
  }
}

export const deduplicateRoomsByParticipant = (
  rooms: Record<string, unknown>[],
  currentUserId: string
): Record<string, unknown>[] => {
  const roomsByParticipant = new Map<string, Record<string, unknown>>()

  rooms.forEach((room: Record<string, unknown>) => {
    const participants = room.participants as string[] | undefined
    const otherParticipantId = participants?.find(
      (p: string) => p !== currentUserId
    )

    if (!otherParticipantId) return

    const existingRoom = roomsByParticipant.get(otherParticipantId)
    const roomTime = getRoomTimestamp(room)
    const existingTime = existingRoom ? getRoomTimestamp(existingRoom) : 0

    if (!existingRoom || roomTime > existingTime) {
      roomsByParticipant.set(otherParticipantId, room)
    }
  })

  return Array.from(roomsByParticipant.values())
}

export const filterRoomsWithMessages = (
  rooms: Record<string, unknown>[]
): Record<string, unknown>[] => {
  return rooms.filter((room: Record<string, unknown>) => {
    const lastMessage = room.lastMessage as string
    return lastMessage && lastMessage.trim() !== ''
  })
}

export const sortContactsByTime = (contacts: ChatContact[]): ChatContact[] => {
  return [...contacts].sort((a, b) => {
    const timeA = a.lastMessageTime || 0
    const timeB = b.lastMessageTime || 0
    return timeB - timeA
  })
}

export const preserveUnreadCounts = (
  newContacts: ChatContact[],
  prevContacts: ChatContact[]
): ChatContact[] => {
  return newContacts.map(newContact => {
    const existingContact = prevContacts.find(
      c => c.roomId === newContact.roomId
    )

    if (existingContact?.unreadCount !== undefined) {
      return {
        ...newContact,
        unreadCount: existingContact.unreadCount,
      }
    }

    return newContact
  })
}

export const markMessageAsRead = async (roomId: string, messageId: string) => {
  if (!roomId || !isFirebaseAvailable()) return

  const db = getFirebaseDb()
  if (!db) return

  try {
    const messageRef = doc(
      db,
      CHATROOM_PATH,
      roomId,
      CHAT_PATHSEGMENTS.MESSAGES,
      messageId
    )
    await updateDoc(messageRef, {
      read: true,
    })
  } catch (err) {
    console.error('Error marking message as read:', err)
  }
}

export const handleChatIntersection = (
  roomId: string,
  entries: IntersectionObserverEntry[]
) => {
  entries.forEach(entry => {
    if (entry.isIntersecting) {
      const messageId = entry.target.getAttribute('data-message-id')
      const isRead = entry.target.getAttribute('data-message-read') === 'true'
      const isOwnMessage =
        entry.target.getAttribute('data-message-own') === 'true'

      if (messageId && !isRead && !isOwnMessage) {
        setTimeout(() => {
          markMessageAsRead(roomId, messageId)
        }, 500)
      }
    }
  })
}

export const convertToChatUserPayload = (
  userData: User
): Partial<ChatUserData> => {
  return {
    id: userData?.id,
    name: `${userData?.firstName} ${userData?.lastName}`,
    profilePicture: userData?.profilePicture,
  }
}

export const transformChatContactUserDetails = async (
  room: Record<string, unknown>,
  currentUserId: string
) => {
  const participants = room.participants as string[] | undefined
  const otherParticipantId = participants?.find(
    (p: string) => p !== currentUserId
  )

  const roomId = room.id as string
  const chatAdminId = room.chatAdminId as string | undefined
  const isActive = room.isActive !== false

  const userData = otherParticipantId
    ? await getChatUserData(otherParticipantId)
    : null

  const isDisabled =
    !isActive || (chatAdminId !== undefined && chatAdminId !== currentUserId)

  return {
    id: otherParticipantId || 'unknown',
    name:
      userData?.name || `User ${otherParticipantId?.slice(-4) || 'Unknown'}`,
    avatar: userData?.profilePicture?.url || undefined,
    lastMessage: (room.lastMessage as string) || '',
    lastMessageTime: getRoomTimestamp(room),
    online: false,
    roomId,
    chatAdminId,
    isDisabled,
    isActive,
  }
}

export const deactivateOtherAdminRooms = async (
  newAdminId: string
): Promise<void> => {
  if (!isFirebaseAvailable()) {
    throw new Error(CHAT_ERRORS.SERVICE_UNAVAILABLE)
  }

  const db = getFirebaseDb()
  if (!db) {
    throw new Error(CHAT_ERRORS.SERVICE_UNAVAILABLE)
  }

  try {
    const chatRoomsRef = collection(db, CHATROOM_PATH)

    const deactivateQuery = query(
      chatRoomsRef,
      where(CHAT_FIELDS.CHAT_ADMIN_ID, '!=', newAdminId)
    )
    const deactivateSnapshot = await getDocs(deactivateQuery)

    const deactivatePromises = deactivateSnapshot.docs.map(docSnapshot => {
      const roomRef = doc(db, CHATROOM_PATH, docSnapshot.id)
      return updateDoc(roomRef, {
        isActive: false,
        deactivatedAt: serverTimestamp(),
      })
    })

    const activateQuery = query(
      chatRoomsRef,
      where(CHAT_FIELDS.CHAT_ADMIN_ID, '==', newAdminId)
    )
    const activateSnapshot = await getDocs(activateQuery)

    const activatePromises = activateSnapshot.docs.map(docSnapshot => {
      const roomRef = doc(db, CHATROOM_PATH, docSnapshot.id)
      return updateDoc(roomRef, {
        isActive: true,
        activatedAt: serverTimestamp(),
      })
    })

    await Promise.all([...deactivatePromises, ...activatePromises])
  } catch (error) {
    console.error('Error updating admin rooms:', error)
    throw error
  }
}
